import { parse, type SC2Table } from "@/games/sc2/parser/index";
import { getLayout, qwerty } from "@/hardware/keyboard-layouts/index";
import { type DerivedLayout, deriveLayout } from "./derive-layout";
import { applyHandMirror } from "./hand-mirror";
import { HAND_MIRROR } from "./hand-mirror-table";
import manifest from "./manifest.json";
// Edition 1.0 seeds: US-Qwerty, both hands vendored. Race is per-edition - 1.0's
// Protoss/Terran/Random files are byte-identical per hand (one "P/T/R" option, seeded from the
// Protoss file); only Zerg differs. Size S/M/L is a real content axis. Editions 1-4 vendor their
// REAL Left seeds rather than mirror-generating them: the learned hand mirror diverges from the
// real Left files by up to ~150 entries on the older editions' Small/Random variants,
// so only editions 5-6 (where the mirror was learned and oracle-validated) generate Left. Other
// locales are generated per-download by convert() and verified against the vendored per-locale
// archive by the old-edition oracle (locale-aliases.ts pairs the directories with layouts).
import v1PtrLeftLarge from "./sources/TheCore_1.0/US Qwerty (Standard)/TheCore PLL .SC2Hotkeys" with {
  type: "text",
};
import v1PtrLeftMedium from "./sources/TheCore_1.0/US Qwerty (Standard)/TheCore PLM .SC2Hotkeys" with {
  type: "text",
};
import v1PtrLeftSmall from "./sources/TheCore_1.0/US Qwerty (Standard)/TheCore PLS .SC2Hotkeys" with {
  type: "text",
};
import v1PtrLarge from "./sources/TheCore_1.0/US Qwerty (Standard)/TheCore PRL .SC2Hotkeys" with {
  type: "text",
};
import v1PtrMedium from "./sources/TheCore_1.0/US Qwerty (Standard)/TheCore PRM .SC2Hotkeys" with {
  type: "text",
};
import v1PtrSmall from "./sources/TheCore_1.0/US Qwerty (Standard)/TheCore PRS .SC2Hotkeys" with {
  type: "text",
};
import v1ZergLeftLarge from "./sources/TheCore_1.0/US Qwerty (Standard)/TheCore ZLL .SC2Hotkeys" with {
  type: "text",
};
import v1ZergLeftMedium from "./sources/TheCore_1.0/US Qwerty (Standard)/TheCore ZLM .SC2Hotkeys" with {
  type: "text",
};
import v1ZergLeftSmall from "./sources/TheCore_1.0/US Qwerty (Standard)/TheCore ZLS .SC2Hotkeys" with {
  type: "text",
};
import v1ZergLarge from "./sources/TheCore_1.0/US Qwerty (Standard)/TheCore ZRL .SC2Hotkeys" with {
  type: "text",
};
import v1ZergMedium from "./sources/TheCore_1.0/US Qwerty (Standard)/TheCore ZRM .SC2Hotkeys" with {
  type: "text",
};
import v1ZergSmall from "./sources/TheCore_1.0/US Qwerty (Standard)/TheCore ZRS .SC2Hotkeys" with {
  type: "text",
};
// Editions 2.0 and 3.0 seeds: US-Qwerty, both hands vendored, four distinct races
// (Protoss/Terran/Random/Zerg) across three sizes (Small/Medium/Large). Unlike 1.0 - where the
// three non-Zerg races shipped one byte-identical "P/T/R" file - every race differs here (verified
// by md5 across all three sizes and both hands), so each is its own option. Other locales stay
// dormant fixtures until later slices generate them. Variable names mirror the vendored 3-letter
// codes: [Race][Hand][Size].
import v2PLL from "./sources/TheCore_2.0/US QWERTY/TheCore PLL .SC2Hotkeys" with { type: "text" };
import v2PLM from "./sources/TheCore_2.0/US QWERTY/TheCore PLM .SC2Hotkeys" with { type: "text" };
import v2PLS from "./sources/TheCore_2.0/US QWERTY/TheCore PLS .SC2Hotkeys" with { type: "text" };
import v2PRL from "./sources/TheCore_2.0/US QWERTY/TheCore PRL .SC2Hotkeys" with { type: "text" };
import v2PRM from "./sources/TheCore_2.0/US QWERTY/TheCore PRM .SC2Hotkeys" with { type: "text" };
import v2PRS from "./sources/TheCore_2.0/US QWERTY/TheCore PRS .SC2Hotkeys" with { type: "text" };
import v2RLL from "./sources/TheCore_2.0/US QWERTY/TheCore RLL .SC2Hotkeys" with { type: "text" };
import v2RLM from "./sources/TheCore_2.0/US QWERTY/TheCore RLM .SC2Hotkeys" with { type: "text" };
import v2RLS from "./sources/TheCore_2.0/US QWERTY/TheCore RLS .SC2Hotkeys" with { type: "text" };
import v2RRL from "./sources/TheCore_2.0/US QWERTY/TheCore RRL .SC2Hotkeys" with { type: "text" };
import v2RRM from "./sources/TheCore_2.0/US QWERTY/TheCore RRM .SC2Hotkeys" with { type: "text" };
import v2RRS from "./sources/TheCore_2.0/US QWERTY/TheCore RRS .SC2Hotkeys" with { type: "text" };
import v2TLL from "./sources/TheCore_2.0/US QWERTY/TheCore TLL .SC2Hotkeys" with { type: "text" };
import v2TLM from "./sources/TheCore_2.0/US QWERTY/TheCore TLM .SC2Hotkeys" with { type: "text" };
import v2TLS from "./sources/TheCore_2.0/US QWERTY/TheCore TLS .SC2Hotkeys" with { type: "text" };
import v2TRL from "./sources/TheCore_2.0/US QWERTY/TheCore TRL .SC2Hotkeys" with { type: "text" };
import v2TRM from "./sources/TheCore_2.0/US QWERTY/TheCore TRM .SC2Hotkeys" with { type: "text" };
import v2TRS from "./sources/TheCore_2.0/US QWERTY/TheCore TRS .SC2Hotkeys" with { type: "text" };
import v2ZLL from "./sources/TheCore_2.0/US QWERTY/TheCore ZLL .SC2Hotkeys" with { type: "text" };
import v2ZLM from "./sources/TheCore_2.0/US QWERTY/TheCore ZLM .SC2Hotkeys" with { type: "text" };
import v2ZLS from "./sources/TheCore_2.0/US QWERTY/TheCore ZLS .SC2Hotkeys" with { type: "text" };
import v2ZRL from "./sources/TheCore_2.0/US QWERTY/TheCore ZRL .SC2Hotkeys" with { type: "text" };
import v2ZRM from "./sources/TheCore_2.0/US QWERTY/TheCore ZRM .SC2Hotkeys" with { type: "text" };
import v2ZRS from "./sources/TheCore_2.0/US QWERTY/TheCore ZRS .SC2Hotkeys" with { type: "text" };
import v3PLL from "./sources/TheCore_3.0/USQwerty/TheCore PLL .SC2Hotkeys" with { type: "text" };
import v3PLM from "./sources/TheCore_3.0/USQwerty/TheCore PLM .SC2Hotkeys" with { type: "text" };
import v3PLS from "./sources/TheCore_3.0/USQwerty/TheCore PLS .SC2Hotkeys" with { type: "text" };
import v3PRL from "./sources/TheCore_3.0/USQwerty/TheCore PRL .SC2Hotkeys" with { type: "text" };
import v3PRM from "./sources/TheCore_3.0/USQwerty/TheCore PRM .SC2Hotkeys" with { type: "text" };
import v3PRS from "./sources/TheCore_3.0/USQwerty/TheCore PRS .SC2Hotkeys" with { type: "text" };
import v3RLL from "./sources/TheCore_3.0/USQwerty/TheCore RLL .SC2Hotkeys" with { type: "text" };
import v3RLM from "./sources/TheCore_3.0/USQwerty/TheCore RLM .SC2Hotkeys" with { type: "text" };
import v3RLS from "./sources/TheCore_3.0/USQwerty/TheCore RLS .SC2Hotkeys" with { type: "text" };
import v3RRL from "./sources/TheCore_3.0/USQwerty/TheCore RRL .SC2Hotkeys" with { type: "text" };
import v3RRM from "./sources/TheCore_3.0/USQwerty/TheCore RRM .SC2Hotkeys" with { type: "text" };
import v3RRS from "./sources/TheCore_3.0/USQwerty/TheCore RRS .SC2Hotkeys" with { type: "text" };
import v3TLL from "./sources/TheCore_3.0/USQwerty/TheCore TLL .SC2Hotkeys" with { type: "text" };
import v3TLM from "./sources/TheCore_3.0/USQwerty/TheCore TLM .SC2Hotkeys" with { type: "text" };
import v3TLS from "./sources/TheCore_3.0/USQwerty/TheCore TLS .SC2Hotkeys" with { type: "text" };
import v3TRL from "./sources/TheCore_3.0/USQwerty/TheCore TRL .SC2Hotkeys" with { type: "text" };
import v3TRM from "./sources/TheCore_3.0/USQwerty/TheCore TRM .SC2Hotkeys" with { type: "text" };
import v3TRS from "./sources/TheCore_3.0/USQwerty/TheCore TRS .SC2Hotkeys" with { type: "text" };
import v3ZLL from "./sources/TheCore_3.0/USQwerty/TheCore ZLL .SC2Hotkeys" with { type: "text" };
import v3ZLM from "./sources/TheCore_3.0/USQwerty/TheCore ZLM .SC2Hotkeys" with { type: "text" };
import v3ZLS from "./sources/TheCore_3.0/USQwerty/TheCore ZLS .SC2Hotkeys" with { type: "text" };
import v3ZRL from "./sources/TheCore_3.0/USQwerty/TheCore ZRL .SC2Hotkeys" with { type: "text" };
import v3ZRM from "./sources/TheCore_3.0/USQwerty/TheCore ZRM .SC2Hotkeys" with { type: "text" };
import v3ZRS from "./sources/TheCore_3.0/USQwerty/TheCore ZRS .SC2Hotkeys" with { type: "text" };
import v4Left from "./sources/TheCore_4.0/US Qwerty/The Core 4.0 Left.SC2Hotkeys" with {
  type: "text",
};
// Edition 4.0 seeds: a structural twin of 5.0 - US-Qwerty, Standard and Plus modifier placement
// (Plus renders as the thecore-plus derived board layer). Both hands vendored: the real Left
// files exist, and the vendored bytes beat even 4.0's near-faithful mirror (3 utility-key diffs).
import v4LeftPlus from "./sources/TheCore_4.0/US Qwerty/The Core 4.0 Left Plus.SC2Hotkeys" with {
  type: "text",
};
import v4Right from "./sources/TheCore_4.0/US Qwerty/The Core 4.0 Right.SC2Hotkeys" with {
  type: "text",
};
import v4RightPlus from "./sources/TheCore_4.0/US Qwerty/The Core 4.0 Right Plus.SC2Hotkeys" with {
  type: "text",
};
// Editions 5 and 6 vendor ONLY their Right US-Qwerty seed: their Left hand is generated by the
// learned mirror and every other locale by convert() from the seed, so the 48 vendored v5
// locale files stay a dormant archive and their Left seeds live only as test fixtures. (The
// older editions above vendor both hands - see the edition 1.0 note.)
import v5RightPlus from "./sources/TheCore_5_Right_Plus_US_Qwerty.SC2Hotkeys" with { type: "text" };
import v5RightStd from "./sources/TheCore_5_Right_US_Qwerty.SC2Hotkeys" with { type: "text" };
import rightQwerty from "./sources/TheCore_6_Right_US_Qwerty.SC2Hotkeys" with { type: "text" };

/** TheCore's Variant tags (domain.md): Source-owned named axes, never core schema. `edition` and
 * `hand` are universal; `race`, `size`, and `modifier` (placement) are per-edition - an edition
 * declares an axis by setting it on its variants (the variant list is the declaration). There is no
 * generic `flavor` tag: edition 6 declares only edition + hand (no race/size/modifier axis).
 * Values are lowercase route-safe tokens (`zerg`, `ptr`, `medium`, `standard`); display labels are
 * derived in the catalog seam (so `ptr` reads "P/T/R"). */
export interface VariantTags {
  edition: string;
  race?: string;
  size?: string;
  modifier?: string;
  hand: string;
}

/** The Variant axes in canonical positional order: edition first (it drives which axes apply), hand
 * last. Ids and routes join the present axes in this order; faceting reads the same order. */
export const VARIANT_AXES = ["edition", "race", "size", "modifier", "hand"] as const;

/** The present Variant axis values, lowercased, in canonical positional order: the id/route stem.
 * Absent axes are omitted, so a ragged edition yields a shorter id/route than a fully-axed one. */
function variantTokens(variant: VariantTags): string[] {
  return VARIANT_AXES.flatMap((axis) => {
    const value = variant[axis];
    return value ? [value.toLowerCase()] : [];
  });
}

/** A derived-lens marker: the renderer reads it to build a synthetic board layer, rather
 * than string-matching the flavor. "thecore-plus" pins the hand-side modifier-remap layer. */
export type ModRemap = "thecore-plus";

/** The validated derived-lens markers; the raw manifest string is checked against this at ingest. */
export function isModRemap(value: string | undefined): value is ModRemap {
  return value === "thecore-plus";
}

export interface TheCoreSetup {
  name: string;
  variant: VariantTags;
  /** Keyboard Layout id to vendored filename; "qwerty" is the canonical serialization. */
  files: Record<string, string>;
  /** When set, the canonical text is GENERATED by mirroring this vendored Right seed, not
   * read from `files.qwerty` - the Left hand of an edition that vendors only its Right seed. */
  mirrorFrom?: string;
  /** Raw derived-lens marker from the manifest; validated to {@link ModRemap} at ingest. */
  modRemap?: string;
}

/** The per-setup metadata shape agreed for the vendored snapshot (HITL). */
export interface TheCoreManifest {
  source: string;
  game: string;
  upstream: { kind: string; note: string };
  snapshotDate: string;
  setups: TheCoreSetup[];
}

/** One vendored per-Keyboard-Layout serialization, served verbatim as a download. */
export interface DownloadAsset {
  layoutId: string;
  label: string;
  fileName: string;
  /** URL path the asset is published under, dev and dist alike. */
  path: string;
}

/** A Preset-to-be: one TheCore setup, canonical text plus its load-time decodes. */
export interface CatalogEntry {
  id: string;
  game: string;
  source: string;
  name: string;
  variant: VariantTags;
  /** Lowercase /game/source then the present Variant axes in canonical order
   * (edition/[race]/[size]/[modifier]/hand); absent axes are omitted. */
  routePath: string;
  /** The canonical US-Qwerty character-space text: the durable truth. */
  text: string;
  table: SC2Table;
  downloads: DownloadAsset[];
  /** Carried from the setup: a derived-lens marker the renderer reads to pin a board layer. */
  modRemap?: ModRemap;
}

/** The vendored seeds by filename; a new edition needs one import line here. Editions 1-4 vendor
 * both hands (the learned mirror is unfaithful on the older editions, so their real Left files
 * ship byte-faithful); editions 5-6 vendor Right only and generate Left (mirrorFrom in the
 * manifest), so their Left seeds live only as test fixtures. */
const SOURCE_TEXTS: Record<string, string> = {
  "TheCore_6_Right_US_Qwerty.SC2Hotkeys": rightQwerty,
  // v5 logical filenames carry the _US_Qwerty suffix so presetDownload regenerates per-layout names.
  "TheCore_5_Right_US_Qwerty.SC2Hotkeys": v5RightStd,
  "TheCore_5_Right_Plus_US_Qwerty.SC2Hotkeys": v5RightPlus,
  // 1.0 vendored files have cryptic names (e.g. "TheCore PRM "); the manifest references clean logical
  // names so downloads read well and presetDownload can swap the locale tag per layout.
  "TheCore_1_PTR_Small_Right_US_Qwerty.SC2Hotkeys": v1PtrSmall,
  "TheCore_1_PTR_Small_Left_US_Qwerty.SC2Hotkeys": v1PtrLeftSmall,
  "TheCore_1_PTR_Medium_Right_US_Qwerty.SC2Hotkeys": v1PtrMedium,
  "TheCore_1_PTR_Medium_Left_US_Qwerty.SC2Hotkeys": v1PtrLeftMedium,
  "TheCore_1_PTR_Large_Right_US_Qwerty.SC2Hotkeys": v1PtrLarge,
  "TheCore_1_PTR_Large_Left_US_Qwerty.SC2Hotkeys": v1PtrLeftLarge,
  "TheCore_1_Zerg_Small_Right_US_Qwerty.SC2Hotkeys": v1ZergSmall,
  "TheCore_1_Zerg_Small_Left_US_Qwerty.SC2Hotkeys": v1ZergLeftSmall,
  "TheCore_1_Zerg_Medium_Right_US_Qwerty.SC2Hotkeys": v1ZergMedium,
  "TheCore_1_Zerg_Medium_Left_US_Qwerty.SC2Hotkeys": v1ZergLeftMedium,
  "TheCore_1_Zerg_Large_Right_US_Qwerty.SC2Hotkeys": v1ZergLarge,
  "TheCore_1_Zerg_Large_Left_US_Qwerty.SC2Hotkeys": v1ZergLeftLarge,
  // 2.0 / 3.0 vendored files carry the same cryptic 3-letter codes as 1.0; the manifest references
  // clean logical names (race spelled out, size, hand, locale) so downloads read well per layout.
  "TheCore_2_Protoss_Small_Right_US_Qwerty.SC2Hotkeys": v2PRS,
  "TheCore_2_Protoss_Small_Left_US_Qwerty.SC2Hotkeys": v2PLS,
  "TheCore_2_Protoss_Medium_Right_US_Qwerty.SC2Hotkeys": v2PRM,
  "TheCore_2_Protoss_Medium_Left_US_Qwerty.SC2Hotkeys": v2PLM,
  "TheCore_2_Protoss_Large_Right_US_Qwerty.SC2Hotkeys": v2PRL,
  "TheCore_2_Protoss_Large_Left_US_Qwerty.SC2Hotkeys": v2PLL,
  "TheCore_2_Terran_Small_Right_US_Qwerty.SC2Hotkeys": v2TRS,
  "TheCore_2_Terran_Small_Left_US_Qwerty.SC2Hotkeys": v2TLS,
  "TheCore_2_Terran_Medium_Right_US_Qwerty.SC2Hotkeys": v2TRM,
  "TheCore_2_Terran_Medium_Left_US_Qwerty.SC2Hotkeys": v2TLM,
  "TheCore_2_Terran_Large_Right_US_Qwerty.SC2Hotkeys": v2TRL,
  "TheCore_2_Terran_Large_Left_US_Qwerty.SC2Hotkeys": v2TLL,
  "TheCore_2_Random_Small_Right_US_Qwerty.SC2Hotkeys": v2RRS,
  "TheCore_2_Random_Small_Left_US_Qwerty.SC2Hotkeys": v2RLS,
  "TheCore_2_Random_Medium_Right_US_Qwerty.SC2Hotkeys": v2RRM,
  "TheCore_2_Random_Medium_Left_US_Qwerty.SC2Hotkeys": v2RLM,
  "TheCore_2_Random_Large_Right_US_Qwerty.SC2Hotkeys": v2RRL,
  "TheCore_2_Random_Large_Left_US_Qwerty.SC2Hotkeys": v2RLL,
  "TheCore_2_Zerg_Small_Right_US_Qwerty.SC2Hotkeys": v2ZRS,
  "TheCore_2_Zerg_Small_Left_US_Qwerty.SC2Hotkeys": v2ZLS,
  "TheCore_2_Zerg_Medium_Right_US_Qwerty.SC2Hotkeys": v2ZRM,
  "TheCore_2_Zerg_Medium_Left_US_Qwerty.SC2Hotkeys": v2ZLM,
  "TheCore_2_Zerg_Large_Right_US_Qwerty.SC2Hotkeys": v2ZRL,
  "TheCore_2_Zerg_Large_Left_US_Qwerty.SC2Hotkeys": v2ZLL,
  "TheCore_3_Protoss_Small_Right_US_Qwerty.SC2Hotkeys": v3PRS,
  "TheCore_3_Protoss_Small_Left_US_Qwerty.SC2Hotkeys": v3PLS,
  "TheCore_3_Protoss_Medium_Right_US_Qwerty.SC2Hotkeys": v3PRM,
  "TheCore_3_Protoss_Medium_Left_US_Qwerty.SC2Hotkeys": v3PLM,
  "TheCore_3_Protoss_Large_Right_US_Qwerty.SC2Hotkeys": v3PRL,
  "TheCore_3_Protoss_Large_Left_US_Qwerty.SC2Hotkeys": v3PLL,
  "TheCore_3_Terran_Small_Right_US_Qwerty.SC2Hotkeys": v3TRS,
  "TheCore_3_Terran_Small_Left_US_Qwerty.SC2Hotkeys": v3TLS,
  "TheCore_3_Terran_Medium_Right_US_Qwerty.SC2Hotkeys": v3TRM,
  "TheCore_3_Terran_Medium_Left_US_Qwerty.SC2Hotkeys": v3TLM,
  "TheCore_3_Terran_Large_Right_US_Qwerty.SC2Hotkeys": v3TRL,
  "TheCore_3_Terran_Large_Left_US_Qwerty.SC2Hotkeys": v3TLL,
  "TheCore_3_Random_Small_Right_US_Qwerty.SC2Hotkeys": v3RRS,
  "TheCore_3_Random_Small_Left_US_Qwerty.SC2Hotkeys": v3RLS,
  "TheCore_3_Random_Medium_Right_US_Qwerty.SC2Hotkeys": v3RRM,
  "TheCore_3_Random_Medium_Left_US_Qwerty.SC2Hotkeys": v3RLM,
  "TheCore_3_Random_Large_Right_US_Qwerty.SC2Hotkeys": v3RRL,
  "TheCore_3_Random_Large_Left_US_Qwerty.SC2Hotkeys": v3RLL,
  "TheCore_3_Zerg_Small_Right_US_Qwerty.SC2Hotkeys": v3ZRS,
  "TheCore_3_Zerg_Small_Left_US_Qwerty.SC2Hotkeys": v3ZLS,
  "TheCore_3_Zerg_Medium_Right_US_Qwerty.SC2Hotkeys": v3ZRM,
  "TheCore_3_Zerg_Medium_Left_US_Qwerty.SC2Hotkeys": v3ZLM,
  "TheCore_3_Zerg_Large_Right_US_Qwerty.SC2Hotkeys": v3ZRL,
  "TheCore_3_Zerg_Large_Left_US_Qwerty.SC2Hotkeys": v3ZLL,
  // 4.0 logical names mirror 5.0's Standard/Plus shape.
  "TheCore_4_Right_US_Qwerty.SC2Hotkeys": v4Right,
  "TheCore_4_Left_US_Qwerty.SC2Hotkeys": v4Left,
  "TheCore_4_Right_Plus_US_Qwerty.SC2Hotkeys": v4RightPlus,
  "TheCore_4_Left_Plus_US_Qwerty.SC2Hotkeys": v4LeftPlus,
};

/** Build catalog entries from a manifest and the vendored texts, validating both. */
export function buildCatalog(
  data: TheCoreManifest,
  texts: Record<string, string>,
): readonly CatalogEntry[] {
  if (data.setups.length === 0) throw new Error("thecore-ingest: manifest lists no setups");
  const entries: CatalogEntry[] = []; // built up then returned as readonly
  const seen = new Set<string>();
  for (const setup of data.setups) {
    for (const [tagKey, tagValue] of Object.entries(setup.variant)) {
      if (!tagValue)
        throw new Error(`thecore-ingest: "${setup.name}" has an empty Variant tag "${tagKey}"`);
    }
    if (!setup.variant.edition || !setup.variant.hand) {
      throw new Error(`thecore-ingest: "${setup.name}" is missing the edition or hand Variant tag`);
    }
    if (setup.modRemap !== undefined && !isModRemap(setup.modRemap)) {
      throw new Error(`thecore-ingest: "${setup.name}" has unknown modRemap "${setup.modRemap}"`);
    }
    const canonicalFile = setup.files.qwerty;
    if (!canonicalFile) {
      throw new Error(`thecore-ingest: "${setup.name}" has no canonical qwerty file`);
    }
    const id = `thecore-${variantTokens(setup.variant).join("-")}`.toLowerCase();
    if (seen.has(id)) throw new Error(`thecore-ingest: duplicate setup id "${id}"`);
    seen.add(id);
    // The canonical text is the vendored Right seed, or - for a generated hand - the mirror of the
    // Right seed named by mirrorFrom. Its download keeps the canonical filename either way.
    let text: string;
    if (setup.mirrorFrom != null) {
      const seed = texts[setup.mirrorFrom];
      if (seed == null) {
        throw new Error(`thecore-ingest: mirrorFrom seed "${setup.mirrorFrom}" is not vendored`);
      }
      text = applyHandMirror(seed, HAND_MIRROR);
    } else {
      const vendored = texts[canonicalFile];
      if (vendored == null) throw new Error(`thecore-ingest: "${canonicalFile}" is not vendored`);
      text = vendored;
    }
    const downloads: DownloadAsset[] = [];
    for (const [layoutId, fileName] of Object.entries(setup.files)) {
      // The canonical file's bytes are `text` (vendored or generated); only a sibling layout file
      // must itself be vendored.
      if (layoutId !== "qwerty" && texts[fileName] == null) {
        throw new Error(`thecore-ingest: "${fileName}" is not vendored`);
      }
      downloads.push({
        layoutId,
        label: getLayout(layoutId)?.name ?? layoutId,
        fileName,
        path: `/downloads/${fileName}`,
      });
    }
    const route = `/${[data.game, data.source, ...variantTokens(setup.variant)].join("/")}`;
    // `table` is parsed lazily and memoized per entry: only the test suite reads it. The app and the
    // prerender re-parse `text` through the active lens at render and never touch `table`, so eagerly
    // parsing every vendored seed here was pure cold-start work. (`text` is the per-iteration `let`
    // above, so each getter closes over its own seed.)
    let cachedTable: SC2Table | undefined;
    entries.push({
      id,
      game: data.game,
      source: data.source,
      name: setup.name,
      variant: { ...setup.variant },
      routePath: route.toLowerCase(),
      text,
      get table() {
        cachedTable ??= parse(text, qwerty);
        return cachedTable;
      },
      downloads,
      ...(isModRemap(setup.modRemap) ? { modRemap: setup.modRemap } : {}),
    });
  }
  return entries;
}

/**
 * Recover each non-canonical sibling's Keyboard Layout by alignment, keyed by
 * Keyboard Layout id. Each derivation is partial (only the codes its file
 * binds), and a later setup's result replaces an earlier one's, losing its
 * coverage; merging votes across setups is deferred until a real sibling
 * drop lands (fixture-first). Hand-authored registry tables stay
 * canonical; this surface validates a drop and will feed the registry then.
 */
export function deriveSiblingLayouts(
  data: TheCoreManifest,
  texts: Record<string, string>,
): Record<string, DerivedLayout> {
  const out: Record<string, DerivedLayout> = {};
  for (const setup of data.setups) {
    const canonicalFile = setup.files.qwerty;
    if (!canonicalFile) continue;
    const canonicalText = texts[canonicalFile];
    if (canonicalText == null) continue;
    for (const [layoutId, fileName] of Object.entries(setup.files)) {
      if (layoutId === "qwerty") continue;
      const siblingText = texts[fileName];
      if (siblingText == null) continue;
      out[layoutId] = deriveLayout(canonicalText, siblingText, {
        id: layoutId,
        name: getLayout(layoutId)?.name ?? layoutId,
      });
    }
  }
  return out;
}

/** The ingested catalog: TheCore setups as Presets-to-be, in manifest order. */
export const catalogEntries: readonly CatalogEntry[] = buildCatalog(manifest, SOURCE_TEXTS);

/** The prerender list: every Preset route's render-to-static step emits. */
export const prerenderRoutes: readonly string[] = catalogEntries.map((entry) => entry.routePath);

/** Every download fileName, deduped across setups; emit and the dev route share it. */
export const downloadFileNames: ReadonlySet<string> = new Set(
  catalogEntries.flatMap((entry) => entry.downloads.map((download) => download.fileName)),
);

/** Each download's file content by name: the vendored Right bytes for a canonical seed, or the
 * generated Left text. server.ts and emit-downloads serve from this, so a generated hand
 * needs no file on disk - the seed-drop that lets an edition vendor only its Right seed. */
export const downloadTexts: ReadonlyMap<string, string> = new Map(
  catalogEntries.flatMap((entry) =>
    entry.downloads.map((d): [string, string] => [
      d.fileName,
      d.layoutId === "qwerty" ? entry.text : (SOURCE_TEXTS[d.fileName] ?? entry.text),
    ]),
  ),
);
